import { ChangeDetectionStrategy, Component, computed, inject, resource, signal, ViewContainerRef } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { DialogSheetComponent, dialogSheetHostClass, HlmDialogOpener } from '@lite-workspace/shared/helm-ui/dialog';
import { HlmBadge, type BadgeVariants } from '@lite-workspace/shared/helm-ui/badge';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmSkeleton } from '@lite-workspace/shared/helm-ui/skeleton';
import { HlmSpinner } from '@lite-workspace/shared/helm-ui/spinner';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { pluralRu } from '@lite-workspace/shared/format';
import {
  copyRefusalOf,
  resolveCopyPrintProduct,
  type CopyRefusal,
  type UpsaleBillDto,
  type UpsaleCopyFormat,
} from '@lite-workspace/dto-fotoclass-book';
import { SalesStore } from '../../upsale/domain/+state/sales.store';
import { ProjectsStore } from '../domain/+state/projects.store';
import { PrintCatalogFacade } from '../domain/application/print-catalog.facade';
import { albumProductId } from '../domain/application/product-geometry-refresh.service';
import type { PhotoBookVariant } from '../domain/infrastructure/projects-api.service';
import { ApproveAlbumDialogComponent, type ApproveAlbumDialogContext } from './approve-album-dialog.component';

export interface AlbumCopiesDialogContext {
  projectId: string;
  bookId: string;
}

/** `own` = copies bought in the album's own format: printed as the album, nothing to build or check. */
type CopyState = PhotoBookVariant['state'] | 'own';

export const CHIP: Record<CopyState, { label: string; variant: BadgeVariants['variant'] }> = {
  none: { label: 'Не сформирована', variant: 'neutral-dot' },
  building: { label: 'Формируем…', variant: 'busy-dot' },
  unreviewed: { label: 'Не проверена', variant: 'turn-dot' },
  approved: { label: 'Утверждена', variant: 'ok-dot' },
  changed: { label: 'Альбом изменился', variant: 'warn-dot' },
  own: { label: 'Печатается как альбом', variant: 'neutral-dot' },
};

const RETRY = 'Почему-то не удалось.. попробуйте снова';

/** A refused build answers the same on retry: its own words, never «попробуйте снова». */
export const COPY_REFUSAL: Record<CopyRefusal, { chip: string; title: string; hint: string }> = {
  'not-fit': {
    chip: 'Не помещается в формат',
    title: 'Альбом не помещается в этот формат',
    hint: 'Измените альбом и обновите копию',
  },
  'not-built': { chip: 'Альбом не собран', title: 'Альбом ещё не собран', hint: 'Копию можно сделать после сборки альбома' },
};

/** The toast of a failed copy refresh: a refusal tells its reason, any other failure asks to retry. */
export function toastCopyRefreshFailure(toast: Pick<ToastService, 'error'>, error: unknown): CopyRefusal | null {
  const refusal: CopyRefusal | null = copyRefusalOf(error);
  if (refusal) toast.error(COPY_REFUSAL[refusal].title, COPY_REFUSAL[refusal].hint);
  else toast.error(RETRY);
  return refusal;
}

export function copyRowHead(variant: Pick<PhotoBookVariant, 'format' | 'copies'>): string {
  return `${variant.format} · ${variant.copies} ${pluralRu(variant.copies, ['копия', 'копии', 'копий'])}`;
}

/**
 * Ordered copies (paid or not) of the album owner printed as the album — core's copies list skips them.
 * A copy resolving to no extra product prints in the album's format, as the purchases page and the archive read it.
 * `format` = the album product's format label; a copy's own `content.format` may hold the tariff name.
 */
export function ownFormatCopies(
  bills: readonly UpsaleBillDto[],
  personId: string | null | undefined,
  ownProductId: string | null | undefined,
  copyFormats: readonly UpsaleCopyFormat[],
  format: string | null | undefined,
): Pick<PhotoBookVariant, 'printProductId' | 'format' | 'copies'> | null {
  if (!personId || !ownProductId || !format) return null;
  const copies = bills
    .filter(bill => bill.personId === personId)
    .flatMap(bill => bill.positions)
    .filter(p => p.kind === 'copy' && [undefined, ownProductId].includes(resolveCopyPrintProduct(p.content, copyFormats)))
    .reduce((n, p) => n + p.qty, 0);
  return copies ? { printProductId: ownProductId, format, copies } : null;
}

@Component({
  selector: 'app-album-copies-dialog',
  imports: [HlmBadge, HlmButton, HlmSkeleton, HlmSpinner, DialogSheetComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 class="mb-5 text-lg font-bold text-ink">Копии альбома</h2>

        @if (rows(); as rows) {
          <div class="flex flex-col gap-3">
            @for (row of rows; track row.printProductId) {
              <section
                class="flex items-center gap-2 rounded-[var(--r-l)] border border-line2 bg-bg2 px-5 py-4"
                [attr.data-copy]="row.printProductId"
                [attr.data-state]="row.state"
              >
                <div class="min-w-0 flex-1">
                  <div class="text-base font-semibold text-ink dlg-desktop:whitespace-nowrap">{{ row.head }}</div>
                  <div class="mt-2 flex">
                    <span hlmBadge [variant]="row.chip.variant">
                      @if (row.state === 'building') {
                        <hlm-spinner tone="c3" [size]="10" />
                      }
                      {{ row.chip.label }}
                    </span>
                  </div>
                </div>
                @if (!row.refused && (row.state === 'none' || row.state === 'changed' || row.state === 'unreviewed')) {
                  <button
                    hlmBtn
                    type="button"
                    [variant]="row.state === 'unreviewed' ? 'outline' : 'default'"
                    (click)="refresh(row.printProductId)"
                  >
                    Обновить
                  </button>
                }
                @if (row.state === 'unreviewed') {
                  <button hlmBtn type="button" (click)="review(row)">Проверить и утвердить</button>
                }
              </section>
            }
          </div>
        } @else if (failed()) {
          <button hlmBtn type="button" (click)="load()">Повторить</button>
        } @else {
          <div hlmSkeleton class="h-21.5 rounded-[var(--r-l)]"></div>
        }

        <div class="mt-6 flex justify-end">
          <button hlmBtn type="button" variant="outline" (click)="dialogRef.close(undefined)">Закрыть</button>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class AlbumCopiesDialogComponent {
  private readonly ctx = injectBrnDialogContext<AlbumCopiesDialogContext>();
  private readonly store = inject(ProjectsStore);
  // The albums page's own instance: its bills are what put «Копии в других форматах» in the album menu.
  private readonly sales = inject(SalesStore);
  private readonly dialog = inject(HlmDialogOpener);
  private readonly vcr = inject(ViewContainerRef);
  private readonly toast = inject(ToastService);
  private readonly printCatalog = inject(PrintCatalogFacade);
  protected readonly dialogRef = inject(BrnDialogRef);

  protected readonly hostClass = dialogSheetHostClass('l', 'p-8');

  // Refresh pressed: the row reads «Формируем…» at once, until the refresh request settles and the list is re-read.
  private readonly refreshing = signal<ReadonlySet<string>>(new Set());
  // Refused builds of this dialog: «Обновить» would only answer the same.
  private readonly refused = signal<ReadonlyMap<string, CopyRefusal>>(new Map());
  // The last copies read failed and nothing is stored: «Повторить» instead of the skeleton.
  protected readonly failed = signal(false);

  protected readonly rows = computed(() => {
    const variants = this.store.variantsByBook()[this.ctx.bookId];
    if (!variants) return null;
    const rows = variants.map(variant => {
      const state: CopyState = this.refreshing().has(variant.printProductId) ? 'building' : variant.state;
      const refusal: CopyRefusal | undefined = state === 'building' ? undefined : this.refused().get(variant.printProductId);
      const chip = refusal ? { label: COPY_REFUSAL[refusal].chip, variant: 'warn-dot' as const } : CHIP[state];
      return { ...variant, state, refused: !!refusal, head: copyRowHead(variant), chip };
    });
    const own = this.ownCopies();
    return own ? [...rows, { ...own, state: 'own' as const, refused: false, head: copyRowHead(own), chip: CHIP.own }] : rows;
  });

  private readonly album = computed(() => this.store.albumsByProject()[this.ctx.projectId]?.find(a => a.id === this.ctx.bookId));

  private readonly ownProduct = computed(() => {
    const album = this.album();
    return (
      (album && albumProductId(album)) ||
      this.store.baseSettingsByProject()[this.ctx.projectId]?.photoBook?.printProductId ||
      undefined
    );
  });

  /** The album product's format, as «Печать» in project settings shows it; null = unknown. */
  private readonly albumFormat = resource({
    params: () => this.ownProduct(),
    loader: ({ params }) =>
      firstValueFrom(this.printCatalog.getProduct(params)).then(
        product => product.formatName,
        () => null,
      ),
  });

  private readonly ownCopies = computed(() => {
    const copyFormats = this.store.upsaleByProject()[this.ctx.projectId]?.goods.copies?.extra ?? [];
    return ownFormatCopies(this.sales.bills(), this.album()?.personId, this.ownProduct(), copyFormats, this.albumFormat.value());
  });

  constructor() {
    void this.load();
  }

  protected async load(): Promise<void> {
    this.failed.set(false);
    // RACE-SAFE: the flag only matters while no list is stored; the store orders the reads themselves.
    const read = await this.store.loadVariants(this.ctx.projectId, this.ctx.bookId);
    if (read) return;
    this.failed.set(true);
    this.toast.error(RETRY);
  }

  protected async refresh(printProductId: string): Promise<void> {
    this.refreshing.update(set => new Set(set).add(printProductId));
    try {
      // The store re-reads the list once the refresh is accepted.
      await this.store.refreshVariant(this.ctx.projectId, this.ctx.bookId, printProductId);
    } catch (error) {
      const refusal: CopyRefusal | null = toastCopyRefreshFailure(this.toast, error);
      if (refusal) this.refused.update(map => new Map(map).set(printProductId, refusal));
    } finally {
      // RACE-SAFE: the overlay only bridges the request; the store's list is the truth once it settles.
      this.refreshing.update(set => {
        const next = new Set(set);
        next.delete(printProductId);
        return next;
      });
    }
  }

  // The review dialog reads the copy's spreads live from the store and approves the copy itself.
  protected review({ printProductId, format }: Pick<PhotoBookVariant, 'printProductId' | 'format'>): void {
    const { projectId, bookId } = this.ctx;
    this.dialog.open<ApproveAlbumDialogContext, boolean>(ApproveAlbumDialogComponent, this.vcr, {
      spreads: [],
      variant: { projectId, bookId, printProductId, format },
    });
  }
}
